import requestsService from '~/services/requestsService';
import { CategoryResponseDto } from '@bitecritic-api/src/categories/dto/category.response.dto';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from '~/hooks/useLocale';

const basePath = 'categories' as const;

/**
 * Fetches all categories
 * @param lang - The language code for localized categories
 * @returns Promise with array of categories
 */
export const fetchCategories = async (
  lang = 'en',
): Promise<CategoryResponseDto[]> => {
  try {
    const response = await requestsService.fetch<CategoryResponseDto[]>(
      `${basePath}?lang=${lang}`,
    );
    return response || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

/**
 * Fetches a single category by ID
 * @param id - The category ID
 * @param lang - The language code for localized category
 * @returns Promise with category data
 */
export const fetchCategoryById = async (
  id: number,
  lang = 'en-US',
): Promise<CategoryResponseDto | null> => {
  try {
    const response = await requestsService.fetch<CategoryResponseDto>(
      `${basePath}/${id}?lang=${lang}`,
    );
    return response;
  } catch (error) {
    console.error(`Error fetching category with ID ${id}:`, error);
    return null;
  }
};

export const categoriesService = {
  fetchCategories,
  fetchCategoryById,
};

export default categoriesService;

export const useCategoriesService = () => {
  const { currentLocale } = useLocale();

  return useQuery({
    queryKey: ['categories', currentLocale.language],
    queryFn: () => fetchCategories(currentLocale.language),
  });
};

export const useCategoryByIdService = (id?: number) => {
  const { currentLocale } = useLocale();

  return useQuery<CategoryResponseDto | null>({
    queryKey: ['categories', id, currentLocale.language],
    queryFn: () => fetchCategoryById(id!, currentLocale.language),
    enabled: !!id && Number.isInteger(id) && id > 0,
  });
};
